import axios, { CancelToken, isCancel } from 'axios';
import Cookies from 'universal-cookie';
import moment from 'moment';
import { SEARCH_RESULT_LIMIT, SEARCH_CONTEXTS, SESSION_FILTERS, SEARCH_HISTORY_STORAGE_KEY } from 'components/utils/constants';
import { getAxiosError, formatFilters, denormalizeContextFilters, getLocalStorageItem, setLocalStorageItem } from 'components/utils/helpers';
import { trackSearchView, trackCustomEvent } from 'components/utils/analytics';
import { NOT_ACCEPTABLE } from 'http-status-codes';
import { showSignIn } from 'store/actions/globalActions';
import {
  SET_CANCEL_SEARCH_SOURCE,
  SET_FILTERS_ARE_OPEN,
  SET_SEARCH_QUERY,
  SET_SEARCH_LOADING,
  SET_SEARCH_ERROR,
  ADD_SEARCH_RESULTS,
  SET_USER_SEARCH_FILTERS,
  REMOVE_SEARCH_FILTER,
  UPDATE_SEARCH_FILTERS,
  SET_FILTER_GROUP,
  RESET_FILTER_GROUP,
  UPDATE_GLOBAL_FILTERS,
  RESET_SEARCH_RESULTS,
  RESET_FILTERS,
  RESET_TRANSIENT_FILTERS,
  INCREMENT_PAGE,
  SET_SEARCH_ID,
  SET_SEARCH_FILTERS,
  RESET_AUTO_REFRESH_DELTA,
} from '../reducers/search';
import { resetUser } from './user';

const TIMEOUT = process.env.SIGNIN_TIMEOUT || 10000;

export const setAndGetCancelSearchSource = source => (dispatch) => {
  const cancelSearchSource = source === undefined ? CancelToken.source() : source;
  dispatch({
    type: SET_CANCEL_SEARCH_SOURCE,
    data: { cancelSearchSource },
  });
  return cancelSearchSource;
};

export const setQuery = query => (dispatch) => {
  dispatch({
    type: SET_SEARCH_QUERY,
    data: { query },
  });
};

export const resetResults = () => (dispatch) => {
  dispatch({
    type: RESET_SEARCH_RESULTS,
  });
};

export const resetFilters = () => (dispatch) => {
  dispatch({
    type: RESET_FILTERS,
  });
};

export const resetSessionFilters = () => (dispatch) => {
  dispatch({
    type: RESET_TRANSIENT_FILTERS,
  });
};

export const setUserFilters = ({ feeds = [], languages = [] }) => ({
  type: SET_USER_SEARCH_FILTERS,
  data: {
    filters: { feeds, languages },
  },
});

export const removeFilter =  (filterKey, name) => (dispatch) => {
  dispatch({
    type: REMOVE_SEARCH_FILTER,
    data: { filterKey, name },
  });
};

export const updateFilter =  (filterKey, name, value) => (dispatch) => {
  dispatch({
    type: UPDATE_SEARCH_FILTERS,
    data: { filterKey, name, value },
  });
};

export const setFilterGroup =  (filterKey, value) => (dispatch) => {
  dispatch({
    type: SET_FILTER_GROUP,
    data: { filterKey, value },
  });
};

export const resetFilterGroups =  filterKeys => (dispatch) => {
  filterKeys.forEach((filterKey) => {
    dispatch({
      type: RESET_FILTER_GROUP,
      data: { filterKey },
    });
  });
};

export const setSearchId =  searchId => (dispatch) => {
  dispatch({
    type: SET_SEARCH_ID,
    data: { searchId },
  });
};

export const updateGlobalFilter = (name, value) => (dispatch, getState) => {
  dispatch({
    type: UPDATE_GLOBAL_FILTERS,
    data: {
      globalFilter: {
        ...getState().search.globalFilter,
        [name]: value,
      },
    },
  });
};

export const toggleFilters = () => (dispatch, getState) => {
  dispatch({
    type: SET_FILTERS_ARE_OPEN,
    data: { filtersAreOpen: !getState().search.filtersAreOpen },
  });
};

export const setFilters = filters => ({
  type: SET_SEARCH_FILTERS,
  data: { filters },
});

export const setSearchLoading = (loading = true) => ({
  type: SET_SEARCH_LOADING,
  data: { loading },
});

export const setSearchError = error => ({
  type: SET_SEARCH_ERROR,
  data: { error },
});

export const setSearchResults = ({ results, totals, prepend, autoRefreshDelta }) => ({
  type: ADD_SEARCH_RESULTS,
  data: { results, totals, prepend, autoRefreshDelta, hasMore: results.length >= SEARCH_RESULT_LIMIT },
});

export const resetAutoRefreshDelta = () => ({
  type: RESET_AUTO_REFRESH_DELTA,
});

export const incrementPage = () => ({
  type: INCREMENT_PAGE,
});

export const performSearch = (prepend = false) => async (dispatch, getState) => {
  dispatch(setSearchLoading());
  try {
    const currentState = getState();
    const { query, totals, globalFilter, filters, results, page } = currentState.search;
    const cancelSearchSource = await dispatch(setAndGetCancelSearchSource());
    const dateRange = prepend ? { after: results.length ? moment(results[0].publishedDate).add(1, 's').format() : /* istanbul ignore next */null } : filters.dateRange;

    const { data } = await axios.post('/api/search',
      {
        query: query.trim(),
        filters: {
          global: {
            ...globalFilter,
            publishedDate: prepend ? moment().format() : globalFilter.publishedDate,
          },
          ...formatFilters({ ...filters }),
          dateRange,
        },
        limit: SEARCH_RESULT_LIMIT,
      }, {
        cancelToken: cancelSearchSource.token,
      });

    dispatch(setAndGetCancelSearchSource(null));

    let newTotals;
    let autoRefreshDelta = 0;
    if (prepend) {
      const { news: currentNews = 0, photos: currentPhotos = 0, videos: currentVideos = 0 } = { ...totals };
      const { totals: { news = 0, photos = 0, videos = 0 } } = data;
      newTotals = { news: currentNews + news, photos: currentPhotos + photos, videos: currentVideos + videos };
      autoRefreshDelta = news + photos + videos;
    } else {
      newTotals = Object.keys(totals).length ? { ...totals } : data.totals;
      dispatch(incrementPage());

      const publishedDate = data.results.length && moment(data.results[data.results.length - 1].publishedDate).subtract(1, 'ms').format() || null;
      if (publishedDate) {
        dispatch(updateGlobalFilter('publishedDate', publishedDate));
      }
    }

    // not tracking autorefresh or subsequent paginated searches
    if (!prepend && page < 1) {
      trackSearchView({
        query,
        filters,
        user: currentState.user.user,
        layout: currentState.globalActions.layout,
        totals: data.totals,
      });
    }

    dispatch(setSearchResults({
      prepend,
      ...data,
      totals: { ...newTotals },
      autoRefreshDelta,
    }));
  } catch (err) {
    if (isCancel(err)) {
      dispatch(setSearchLoading(false));
    } else {
      const { message, status } = getAxiosError(err);
      if (status === NOT_ACCEPTABLE) {
        dispatch(setSearchLoading(false));
        dispatch(resetUser());
        const cookies = new Cookies();
        cookies.remove('login');
        dispatch(showSignIn(true));
      } else {
        console.error(err); // TODO used for debugging
        dispatch(setSearchError(message));
      }
    }
  }
};

export const paginate = () => async (dispatch, getState) => {
  if (getState().search.loading) {
    return;
  }
  dispatch(performSearch());
};

export const updateSearchResults = () => async (dispatch, getState) => {
  if (getState().search.loading) {
    return;
  }

  trackCustomEvent({
    category: 'search',
    action: 'autorefresh',
  });
  dispatch(performSearch(true));
};

const addFiltersToStorage = (filters) => {
  // session filters are not saved in the cookie
  Object.keys(filters).forEach((filterKey) => {
    if (SESSION_FILTERS.includes(filterKey)) {
      /* eslint no-param-reassign: 0 */ // local func, also caller spread the argument to this func
      delete filters[filterKey];
    }
  });

  const searchHistory = getLocalStorageItem(SEARCH_HISTORY_STORAGE_KEY);
  setLocalStorageItem(SEARCH_HISTORY_STORAGE_KEY, { ...searchHistory, filters: { ...searchHistory.filters, ...filters, dateRange: {} } });
};

export const search = () => async (dispatch, getState) => {
  if (getState().search.loading) {
    getState().search.cancelSearchSource.cancel();
  }

  dispatch(resetResults());
  dispatch(updateGlobalFilter('publishedDate', moment().format()));

  const { filters } = getState().search;

  addFiltersToStorage({ ...filters });
  dispatch(performSearch());
};

export const populateSavedSearch = ({ queryParams: { query, filters: savedFilters } }) => async (dispatch, getState) => {
  dispatch(resetFilters());
  dispatch(setQuery(query));

  const { filters } = getState().search;
  const { languages, feeds, contexts = {}, ...basicFilters } = savedFilters;

  Object.keys(basicFilters).forEach((filterKey) => {
    const values = basicFilters[filterKey];
    Object.keys(values).forEach(key => dispatch(updateFilter(filterKey, key, values[key])));
  });

  if (filters.feeds && feeds) {
    Object.keys(filters.feeds).forEach((key) => {
      dispatch(updateFilter('feeds', key, feeds.includes(key)));
    });
  }

  if (filters.languages && languages) {
    Object.keys(filters.languages).forEach((key) => {
      dispatch(updateFilter('languages', key, languages.includes(key)));
    });
  }
  const { topics, companies, people, series, brands } = denormalizeContextFilters(contexts);
  dispatch(setFilterGroup(SEARCH_CONTEXTS.topics.name, topics));
  dispatch(setFilterGroup(SEARCH_CONTEXTS.companies.name, companies));
  dispatch(setFilterGroup(SEARCH_CONTEXTS.people.name, people));
  dispatch(setFilterGroup(SEARCH_CONTEXTS.series.name, series));
  dispatch(setFilterGroup(SEARCH_CONTEXTS.brands.name, brands));
};

export const executeSavedSearch = id => async (dispatch) => {
  try {
    const { data: savedSearchData } = await axios.get(`/api/searches/${id}`, { timeout: TIMEOUT });

    dispatch(setSearchId(id));
    dispatch(populateSavedSearch(savedSearchData));

    trackCustomEvent({
      category: 'savedsearch',
      action: 'execute',
      label: savedSearchData.name,
    }, {
      id,
    });

    dispatch(search());
  } catch (err) {
    const { message } = getAxiosError(err);
    dispatch(setSearchError(message));
  }
};
